import {
    NO,
    UIColor,
    UICore,
    UIRoute,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    UIViewController,
    YES
} from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import type { CBColumnDescriptor } from "../Custom components/CB/CBDataView"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHFilterChipBarView } from "../Custom components/RH/RHFilterChipBarView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import { formatRHDateTime } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHClipboard } from "../Custom components/RH/Utils_slop/RHClipboard"
import { RHTimestampSorting } from "../Custom components/RH/Utils_slop/RHTimestampSorting"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHDraftProcurementsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { NewProcurementViewController } from "../NewProcurement/NewProcurementViewController"
import { ProcurementExcelExportDownloader } from "../ProcurementExcelExport/ProcurementExcelExportDownloader"
import type { RHProcurerDashboardProcurement } from "../SocketClientFunctions"
import { UserMainViewController } from "./UserMainViewController"
import { populatedValueOfReference } from "../Core/RHReference"


type DraftCategory = "draft" | "active-template" | "archived-template"

type DraftActionsMenuItem = {
    title: string
    enabled?: boolean
    disabledReason?: string
    action: () => void | Promise<void>
}


export class DraftActionsMenuView extends UIView {

    readonly _backdrop = new UIView().configuredWithObject({
        backgroundColor: UIColor.transparentColor,
        controlEventTargetAccumulator: {
            PointerUpInside: () => this.dismiss()
        }
    })
    _rows: RHButton[] = []
    _items: DraftActionsMenuItem[] = []
    _trigger?: RHButton
    _keyboardHandler?: (sender: UIView, event: Event) => void
    _scrollHandler?: (sender: UIView, event: Event) => void
    _scrollRoot?: UIView
    _focusedIndex = 0


    constructor() {
        super()
        const tokens = RHDraftProcurementsViewTokens.actionsMenu
        this.backgroundColor = tokens.backgroundColor
        this.style.borderRadius = tokens.borderRadius
        this.style.boxShadow = tokens.boxShadow
        this.style.zIndex = tokens.zIndex.toString()
        this.style.overflow = "hidden"
        this.viewHTMLElement.setAttribute("role", "menu")
    }


    present(trigger: RHButton, items: DraftActionsMenuItem[]) {
        this.dismiss(NO)
        this._trigger = trigger
        trigger.viewHTMLElement.setAttribute("aria-haspopup", "menu")
        trigger.viewHTMLElement.setAttribute("aria-expanded", "true")
        this._items = items
        this._rows = items.map((item, index) => {
            const row = new RHButton().configuredWithObject({
                titleLabel: {
                    text: item.title,
                    textAlignment: UITextView.textAlignment.left
                },
                variant: "light",
                contentPadding: RHButtons.compactContentPadding,
                controlEventTargetAccumulator: {
                    PrimaryActionTriggered: () => this._activateItem(index)
                }
            }).addedAsSubviewToView(this)
            row.viewHTMLElement.setAttribute("role", "menuitem")
            if (item.enabled === NO) {
                row.alpha = 0.56
                row.viewHTMLElement.setAttribute("aria-disabled", "true")
                row.viewHTMLElement.setAttribute("title", item.disabledReason ?? "Toiming pole saadaval")
            }
            return row
        })

        const rootView = UICore.main.rootViewController.view
        const tokens = RHDraftProcurementsViewTokens.actionsMenu
        const triggerRect = trigger.viewHTMLElement.getBoundingClientRect()
        const rootRect = rootView.viewHTMLElement.getBoundingClientRect()
        const pageScale = UIView.pageScale || 1
        const viewportLeft = -rootRect.left / pageScale
        const viewportTop = -rootRect.top / pageScale
        const viewportRight = viewportLeft + window.innerWidth / pageScale
        const viewportBottom = viewportTop + window.innerHeight / pageScale
        const triggerRight = (triggerRect.right - rootRect.left) / pageScale
        const triggerTop = (triggerRect.top - rootRect.top) / pageScale
        const triggerBottom = (triggerRect.bottom - rootRect.top) / pageScale
        const menuHeight = tokens.contentInset * 2 +
            items.length * tokens.rowHeight +
            Math.max(0, items.length - 1) * tokens.gap
        let x = triggerRight - tokens.width
        let y = triggerBottom + tokens.anchorGap
        x = Math.max(
            viewportLeft + tokens.viewportInset,
            Math.min(x, viewportRight - tokens.width - tokens.viewportInset)
        )
        if (y + menuHeight > viewportBottom - tokens.viewportInset) {
            y = triggerTop - menuHeight - tokens.anchorGap
        }
        y = Math.max(
            viewportTop + tokens.viewportInset,
            Math.min(y, viewportBottom - menuHeight - tokens.viewportInset)
        )

        this._backdrop.frame = rootView.bounds
        this._backdrop.style.zIndex = (tokens.zIndex - 1).toString()
        this._backdrop.addedAsSubviewToView(rootView)
        this.frame = rootView.bounds
            .rectangleWithX(x)
            .rectangleWithY(y)
            .rectangleWithHeight(menuHeight)
            .rectangleWithWidth(tokens.width)
        // UIView.frame writes its own z-index. Reapply the overlay layer after
        // assigning the frame so the outside-click backdrop cannot cover the
        // interactive menu rows.
        this.style.zIndex = tokens.zIndex.toString()
        this.addedAsSubviewToView(rootView)

        this._keyboardHandler = (_sender, event) => this._handleKeyboardEvent(event as KeyboardEvent)
        this.addTargetForControlEvents([
            UIView.controlEvent.EscDown,
            UIView.controlEvent.DownArrowDown,
            UIView.controlEvent.UpArrowDown,
            UIView.controlEvent.EnterDown,
            UIView.controlEvent.SpaceDown
        ], this._keyboardHandler)
        this.viewHTMLElement.onkeydown = event => {
            if (event.key === "Home" || event.key === "End") {
                this._handleKeyboardEvent(event)
            }
        }
        this._scrollHandler = () => this.dismiss()
        this._scrollRoot = rootView
        rootView.addTargetForControlEvent(UIView.controlEvent.ScrollWheel, this._scrollHandler)
        this._focusedIndex = 0
        this._rows.firstElement?.focus()
    }


    _activateItem(index: number) {
        const item = this._items[index]
        if (!item || item.enabled === NO) {
            return
        }
        this.dismiss(NO)
        void item.action()
    }


    _handleKeyboardEvent(event: KeyboardEvent) {
        if (this.superview === undefined) {
            return
        }
        if (event.key === "Escape") {
            event.preventDefault()
            this.dismiss()
            return
        }
        if (event.key === "Home") {
            event.preventDefault()
            this._focusedIndex = 0
        }
        else if (event.key === "End") {
            event.preventDefault()
            this._focusedIndex = Math.max(0, this._rows.length - 1)
        }
        else if (event.key === "ArrowDown") {
            event.preventDefault()
            this._focusedIndex = (this._focusedIndex + 1) % this._rows.length
        }
        else if (event.key === "ArrowUp") {
            event.preventDefault()
            this._focusedIndex = (this._focusedIndex - 1 + this._rows.length) % this._rows.length
        }
        else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            this._activateItem(this._focusedIndex)
            return
        }
        else {
            return
        }
        this._rows[this._focusedIndex]?.focus()
    }


    dismiss(restoresFocus: boolean = YES) {
        const trigger = this._trigger
        const triggerElementID = trigger?.viewHTMLElement.id
        trigger?.viewHTMLElement.setAttribute("aria-expanded", "false")
        // Move focus away from the active menuitem before removing that DOM
        // subtree. Otherwise the browser can reset focus to body while the
        // focused row is detached, after our restoration attempt.
        if (restoresFocus) {
            trigger?.focus()
        }
        this._rows.everyElement.removeFromSuperview()
        this._rows = []
        this._items = []
        this._backdrop.removeFromSuperview()
        this.removeFromSuperview()
        if (this._keyboardHandler) {
            this.removeTargetForControlEvents([
                UIView.controlEvent.EscDown,
                UIView.controlEvent.DownArrowDown,
                UIView.controlEvent.UpArrowDown,
                UIView.controlEvent.EnterDown,
                UIView.controlEvent.SpaceDown
            ], this._keyboardHandler)
            this._keyboardHandler = undefined
        }
        this.viewHTMLElement.onkeydown = null
        if (this._scrollHandler && this._scrollRoot) {
            this._scrollRoot.removeTargetForControlEvent(
                UIView.controlEvent.ScrollWheel,
                this._scrollHandler
            )
            this._scrollHandler = undefined
            this._scrollRoot = undefined
        }
        this._trigger = undefined
        if (restoresFocus && triggerElementID) {
            // RHDataView may recycle the action cell during the overlay-removal
            // layout pass. Restore to the current DOM node with the trigger's
            // stable ID after that pass, not only to the possibly recycled view.
            const restoreTriggerFocusIfNeeded = () => {
                if (document.activeElement === document.body || !document.activeElement) {
                    document.getElementById(triggerElementID)?.focus({ preventScroll: YES })
                }
            }
            window.requestAnimationFrame(() => window.requestAnimationFrame(restoreTriggerFocusIfNeeded))
            // The table's deferred cell-layout pass can run after two animation
            // frames and replace the focused button. Repair only neutral body
            // focus so a user's subsequent deliberate focus move is never stolen.
            window.setTimeout(restoreTriggerFocusIfNeeded, 100)
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHDraftProcurementsViewTokens.actionsMenu
        this.contentBoundsWithInset(tokens.contentInset).framesByDistributingViewsAsColumn(
            this._rows,
            tokens.gap,
            this._rows.map(() => tokens.rowHeight)
        )
    }
}


class DraftTableActionButton extends RHButton {

    override updateContentForFocusedState() {
        // RHButton's full focused-state refresh changes several measured styles.
        // Inside UITableView that can schedule a row layout which temporarily
        // detaches the row and immediately discards native focus. A focus outline
        // alone is sufficient here and does not invalidate table geometry.
        const variantTokens = RHButtons[this.variant as keyof typeof RHButtons] as
            { focusOutline?: string } | undefined
        this.style.outline = variantTokens?.focusOutline ?? RHButtons.focusOutline
        this.style.outlineOffset = RHButtons.focusOutlineOffset
    }
}


export class DraftProcurementActionsView extends UIView {

    readonly primaryButton = new DraftTableActionButton().addedAsSubviewToView(this).configuredWithObject({
        titleLabel: { style: { whiteSpace: "nowrap", fontWeight: RHButtons.tableActionTitleFontWeight } },
        variant: RHButtons.tableActionVariant,
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    })
    readonly secondaryButton = new DraftTableActionButton().configuredWithObject({
        titleLabel: { style: { whiteSpace: "nowrap", fontWeight: RHButtons.tableActionTitleFontWeight } },
        variant: "secondary",
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    })
    readonly actionsMenu = new DraftActionsMenuView()
    primaryButtonWasPressed?: () => void
    secondaryButtonWasPressed?: () => void


    constructor() {
        super()
        this.primaryButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.primaryButtonWasPressed?.()
        }
        this.secondaryButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.secondaryButtonWasPressed?.()
        }
    }


    set showsPrimaryButton(value: boolean) {
        if (value) {
            this.primaryButton.addedAsSubviewToView(this)
        }
        else {
            this.primaryButton.removeFromSuperview()
        }
        this.setNeedsLayout()
    }


    set showsSecondaryButton(value: boolean) {
        if (value) {
            this.secondaryButton.addedAsSubviewToView(this)
        }
        else {
            this.secondaryButton.removeFromSuperview()
        }
        this.setNeedsLayout()
    }


    dismissMenu() {
        this.actionsMenu.dismiss(NO)
    }


    configureForDraftRow() {
        const tokens = RHDraftProcurementsViewTokens.draftRowActions
        this.primaryButton.variant = tokens.continueVariant
        this.secondaryButton.variant = tokens.deleteVariant
        this.primaryButton.contentPadding = tokens.contentPadding
        this.secondaryButton.contentPadding = tokens.contentPadding
        this.setNeedsLayout()
    }


    configureForTemplateRow() {
        const tokens = RHDraftProcurementsViewTokens.draftRowActions
        this.primaryButton.variant = tokens.continueVariant
        this.secondaryButton.variant = "secondary"
        this.primaryButton.contentPadding = tokens.contentPadding
        this.secondaryButton.contentPadding = tokens.contentPadding
        this.setNeedsLayout()
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHDraftProcurementsViewTokens.draftRowActions
        const actionButtonFrames = this.contentBoundsWithInset(0)
            .rectangleWithHeight(tokens.height, 0.5)
            .rectangleWithWidth(tokens.width * 2 + tokens.gap, 0.5)
            .rectanglesByEquallySplittingWidth(2, tokens.gap)
        actionButtonFrames[0]
            .IF(this.primaryButton.superview === this)
            .assignedAsFrameOfView(this.primaryButton)
            .ENDIF()
        actionButtonFrames[1]
            .IF(this.secondaryButton.superview === this)
            .assignedAsFrameOfView(this.secondaryButton)
            .ENDIF()
    }


    override intrinsicContentHeight() {
        return RHButtons.tableActionHeight
    }
}


class DraftProcurementsDataView extends RHDataView<RHProcurerDashboardProcurement> {

    readonly headerActionButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Loo uus mall",
                textAlignment: UITextView.textAlignment.center,
                style: {
                    whiteSpace: "nowrap",
                    fontWeight: RHDraftProcurementsViewTokens.headerAction.fontWeight
                }
            },
            variant: RHDraftProcurementsViewTokens.headerAction.variant,
            contentPadding: RHDraftProcurementsViewTokens.headerAction.contentPadding,
            style: {
                fontSize: RHDraftProcurementsViewTokens.headerAction.fontSize
            },
            hidden: YES,
            enabled: NO
        })
        .addedAsSubviewToView(this)


    override layoutSubviews() {
        super.layoutSubviews()
        if (this.headerActionButton.hidden) {
            return
        }

        const tokens = RHDraftProcurementsViewTokens.headerAction
        const titleFrame = this.titleLabel.frame
        const tableAlignedTitleFrame = titleFrame.rectangleWithInsets(
            0,
            this.tableHeaderView.sideCellsWidth,
            0,
            0
        )
        const headerActionButtonWidth =
            this.headerActionButton.titleLabel.intrinsicContentWidth(tokens.height) +
            tokens.contentPadding * 2 +
            tokens.widthSafetyBuffer

        this.headerActionButton.frame = tableAlignedTitleFrame
            .rectangleWithWidth(headerActionButtonWidth, 1)
            .rectangleWithHeight(tokens.height, 0.5)
        this.titleLabel.frame = titleFrame.rectangleWithWidth([
            0,
            tableAlignedTitleFrame.width - headerActionButtonWidth - tokens.gap
        ].max())
    }

}


export class DraftProcurementsViewController extends UIViewController {

    static override readonly routeComponentName = "draft_procurements"
    static override readonly ParameterIdentifierName = {}

    _showsCompanyColumn = NO
    _isAllCompaniesScopeActive = NO
    _hasAppliedDefaultCompanyScope = NO
    _allRows: RHProcurerDashboardProcurement[] = []
    _activeCategories = new Set<DraftCategory>()
    _pendingActions = new Set<string>()
    _canCreateTemplate = NO
    _openActionsView?: DraftProcurementActionsView

    readonly procurementsDataView = new DraftProcurementsDataView()
        .addedAsSubviewToView(this.view)
        .configuredWithObject({
            titleLabel: { text: "Ettevalmistamisel ja mallid" },
            isSearchAvailable: YES,
            descriptors: this._buildDescriptors()
        })

    readonly filterDraftsButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Ettevalmistamisel" }
    })
    readonly filterTemplatesButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Mallid" }
    })
    readonly filterArchivedButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Arhiveeritud" }
    })
    readonly filterAllCompaniesButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Kõik minu ettevõtted" },
        enabled: NO
    })
    readonly filterChipBar = new RHFilterChipBarView(this.view.elementID + "DraftFilterChipBar")
        .addedAsSubviewToView(this.view)

    readonly createTemplateButton = this.procurementsDataView.headerActionButton.configuredWithObject({
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => this._openTemplateAuthoring()
        }
    })

    readonly errorBanner = new RHAlertBannerView({ alertType: RHAlertBannerView.type.Error })
    readonly retryButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Proovi uuesti" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactContentPadding,
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => void this._reloadData()
        }
    })
    readonly bottomView = new UIView().addedAsSubviewToView(this.view)


    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (!this.createTemplateButton.hidden && this.createTemplateButton.isMemberOfViewTree) {
            items.push(this.createTemplateButton)
        }
        items.push(this.filterDraftsButton, this.filterTemplatesButton, this.filterArchivedButton)
        if (this.filterAllCompaniesButton.superview === this.filterChipBar) {
            items.push(this.filterAllCompaniesButton)
        }
        if (this.retryButton.superview === this.view) {
            items.push(this.retryButton)
        }
        items.push(this.procurementsDataView.searchTextField, this.procurementsDataView._gridContainerView)
        return items
    })


    constructor(view: UIView) {
        super(view)
        this.view.backgroundColor = RHColor.background
        this.filterChipBar.chips = this._visibleFilterChips()
        this._wireCategoryButton(this.filterDraftsButton, "draft")
        this._wireCategoryButton(this.filterTemplatesButton, "active-template")
        this._wireCategoryButton(this.filterArchivedButton, "archived-template")
        this.filterAllCompaniesButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._isAllCompaniesScopeActive = NO == this._isAllCompaniesScopeActive
            this.filterAllCompaniesButton.chipActive = this._isAllCompaniesScopeActive
            this._updateCompanyColumnVisibility(
                RHCore.sharedInstance.companyAffiliationStore.hasMultipleCompanyAffiliations &&
                this._isAllCompaniesScopeActive
            )
            void this._reloadData()
        }
        this.procurementsDataView.didUpdateTableAfterSearchTextChange = () => {
            this._updateNoDataMessage()
        }
        this._applyTableColumnLayout()
        this._applyCategoryFilter()
    }


    _wireCategoryButton(button: RHFilterChipButton, category: DraftCategory) {
        button.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            if (this._activeCategories.has(category)) {
                this._activeCategories.delete(category)
            }
            else {
                this._activeCategories.add(category)
            }
            button.chipActive = this._activeCategories.has(category)
            this._applyCategoryFilter()
        }
    }


    _visibleFilterChips(): RHFilterChipButton[] {
        return [
            this.filterDraftsButton,
            this.filterTemplatesButton,
            this.filterArchivedButton,
            this.filterAllCompaniesButton
        ]
    }


    _buildDescriptors(): CBColumnDescriptor<RHProcurerDashboardProcurement>[] {
        const companyDescriptor: CBColumnDescriptor<RHProcurerDashboardProcurement>[] = []
        if (this._showsCompanyColumn) {
            companyDescriptor.push(CBDataViewCellDescriptorWithObject({
                title: "Ettevõte",
                keyPath: "procuringCompany.name",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.text = populatedValueOfReference(dataItem.procuringCompany)?.name || "-"
                }
            }))
        }
        return [
            CBDataViewCellDescriptorWithObject({
                title: "Loomise aeg",
                keyPath: "createdAt",
                allowSorting: YES,
                dataType: "string",
                initialOrderingState: "descending",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const creationDate = this._creationDateForProcurement(dataItem)
                    if (creationDate) {
                        cellView.text = formatRHDateTime(creationDate)
                    }
                    else {
                        cellView.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Tüüp",
                keyPath: "status",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (DraftProcurementsViewController._isTemplate(dataItem)) {
                        cellView.text = "Mall"
                    }
                    else {
                        cellView.text = "Ettevalmistamisel"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Nimetus",
                keyPath: "searchText",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    let title = dataItem.title
                    if (DraftProcurementsViewController._isTemplate(dataItem)) {
                        title = dataItem.templateName ?? ""
                    }
                    cellView.text = RHTextSanitizer.escapedHTML(title || "Nimetus puudub")
                    cellView.configureAsClampedMultilineText()
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Seisund",
                keyPath: "templateLifecycleStatus",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (!DraftProcurementsViewController._isTemplate(dataItem)) {
                        cellView.text = "Ettevalmistamisel"
                    }
                    else if (dataItem.templateLifecycleStatus === "archived") {
                        cellView.text = "Arhiveeritud"
                    }
                    else {
                        cellView.text = "Kasutuses"
                    }
                }
            }),
            ...companyDescriptor,
            CBDataViewCellDescriptorWithObject<
                RHProcurerDashboardProcurement,
                DraftProcurementActionsView
            >({
                title: "Tegevused",
                keyPath: "_id",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new DraftProcurementActionsView(),
                updateCell: (cellView, dataItem) => {
                    const actionsView = cellView.customView
                    actionsView.dismissMenu()
                    const rowHasPendingAction = this._rowHasPendingAction(dataItem)
                    if (!DraftProcurementsViewController._isTemplate(dataItem)) {
                        actionsView.configureForDraftRow()
                        actionsView.showsPrimaryButton = dataItem.capabilities.canModifyDraft
                        actionsView.showsSecondaryButton = dataItem.capabilities.canModifyDraft
                        actionsView.primaryButton.enabled = !rowHasPendingAction
                        actionsView.secondaryButton.enabled = !rowHasPendingAction
                        actionsView.primaryButton.titleLabel.text = "Jätka"
                        actionsView.secondaryButton.titleLabel.text = "Tühista"
                        actionsView.primaryButtonWasPressed = () => this._continueDraft(dataItem)
                        actionsView.secondaryButtonWasPressed = () => this._confirmDraftCancellation(dataItem)
                        return
                    }

                    actionsView.configureForTemplateRow()
                    actionsView.showsPrimaryButton = dataItem.capabilities.canUseTemplate
                    actionsView.showsSecondaryButton = NO
                    actionsView.primaryButton.enabled = !rowHasPendingAction
                    actionsView.secondaryButton.enabled = !rowHasPendingAction
                    actionsView.primaryButton.titleLabel.text = "Vaata"
                    actionsView.primaryButtonWasPressed = () => this._useTemplate(dataItem)
                }
            })
        ]
    }


    static _isTemplate(procurement: RHProcurerDashboardProcurement): boolean {
        return procurement.status === "template"
    }


    static _categoryForRow(procurement: RHProcurerDashboardProcurement): DraftCategory {
        if (!DraftProcurementsViewController._isTemplate(procurement)) {
            return "draft"
        }
        if (procurement.templateLifecycleStatus === "archived") {
            return "archived-template"
        }
        return "active-template"
    }


    _applyCategoryFilter() {
        let rows = this._allRows
        if (this._activeCategories.size === 0) {
            rows = rows.filter(row => DraftProcurementsViewController._categoryForRow(row) !== "archived-template")
        }
        else {
            rows = rows.filter(row => this._activeCategories.has(
                DraftProcurementsViewController._categoryForRow(row)
            ))
        }
        this.procurementsDataView.data = rows.map(row => ({
            ...row,
            searchText: [row.templateName, row.title].filter(value => !!value).join(" ")
        }))
        this._updateNoDataMessage()
    }


    _templateMenuItems(procurement: RHProcurerDashboardProcurement): DraftActionsMenuItem[] {
        const capabilities = procurement.capabilities
        const items: DraftActionsMenuItem[] = []
        if (capabilities.canEditTemplate && procurement.templateLifecycleStatus === "active") {
            items.push({ title: "Muuda", action: () => this._editTemplate(procurement) })
        }
        if (capabilities.canExportTemplateUsage) {
            items.push({
                title: "Laadi XLSX",
                enabled: procurement.hasPublishedTemplateUsage,
                disabledReason: "Malli põhjal pole veel avaldatud hankeid.",
                action: () => this._exportTemplateUsage(procurement)
            })
        }
        if (capabilities.canCopyTemplateLink) {
            items.push({ title: "Kopeeri link", action: () => this._copyTemplateLink(procurement) })
        }
        if (capabilities.canArchiveTemplate && procurement.templateLifecycleStatus === "active") {
            items.push({ title: "Arhiveeri", action: () => this._confirmArchiveTemplate(procurement) })
        }
        if (capabilities.canRestoreTemplate && procurement.templateLifecycleStatus === "archived") {
            items.push({ title: "Taasta", action: () => this._restoreTemplate(procurement) })
        }
        if (capabilities.canDeleteTemplate) {
            items.push({ title: "Kustuta", action: () => this._confirmDeleteTemplate(procurement) })
        }
        return items
    }


    _companyIdForRow(procurement: RHProcurerDashboardProcurement): string | undefined {
        const company = procurement.procuringCompany
        return (company?._id ?? company)?.toString()
    }


    _activateRowCompany(procurement: RHProcurerDashboardProcurement): string | undefined {
        const companyId = this._companyIdForRow(procurement)
        if (!companyId) {
            this._showError("Ettevõtte konteksti ei leitud.")
            return undefined
        }
        const affiliationStore = RHCore.sharedInstance.companyAffiliationStore
        if (affiliationStore.activeCompanyId !== companyId) {
            affiliationStore.deactivateDefaultAllCompaniesScope()
            affiliationStore.activateCompany(companyId)
            if (affiliationStore.activeCompanyId !== companyId) {
                this._showError("Ettevõtte konteksti vahetamine ebaõnnestus.")
                return undefined
            }
        }
        return companyId
    }


    _openTemplateAuthoring() {
        if (!this._canCreateTemplate) {
            return
        }
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(UserMainViewController.routeComponentName)
            .routeWithViewControllerComponent(NewProcurementViewController, { templateMode: "1" })
            .apply()
    }


    _continueDraft(procurement: RHProcurerDashboardProcurement) {
        if (!this._activateRowCompany(procurement)) {
            return
        }
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(UserMainViewController.routeComponentName)
            .routeWithViewControllerComponent(NewProcurementViewController, { id: procurement._id! + "" })
            .apply()
    }


    _useTemplate(procurement: RHProcurerDashboardProcurement) {
        if (!this._activateRowCompany(procurement)) {
            return
        }
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(UserMainViewController.routeComponentName)
            .routeWithViewControllerComponent(NewProcurementViewController, {
                templateId: procurement._id! + ""
            })
            .apply()
    }


    _editTemplate(procurement: RHProcurerDashboardProcurement) {
        if (!this._activateRowCompany(procurement)) {
            return
        }
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(UserMainViewController.routeComponentName)
            .routeWithViewControllerComponent(NewProcurementViewController, { id: procurement._id! + "" })
            .apply()
    }


    _confirmDraftCancellation(procurement: RHProcurerDashboardProcurement) {
        const shower = CBDialogViewShower.showQuestionDialog(
            "Kas soovid hanke koostamise tühistada? Hange jääb ajalukku seisundiga „Tühistatud“."
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Tühista"
        shower.dialogView.view.noButton.titleLabel.text = "Sulge"
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._cancelDraft(procurement)
        }
    }


    async _cancelDraft(procurement: RHProcurerDashboardProcurement) {
        if (!this._activateRowCompany(procurement)) {
            return
        }
        const actionKey = this._actionKey(procurement, "cancel-draft")
        if (!this._beginAction(actionKey)) {
            return
        }
        try {
            const result = await RHCore.sharedInstance.procurementStore.cancelDraftProcurementWithId(procurement._id! + "")
            await this._reloadData()
            if (result.isCancellationConfirmedByRefresh) {
                CBDialogViewShower.alert("Hange on juba tühistatud.")
            }
        }
        catch (error) {
            console.error("DraftProcurementsViewController: draft cancellation failed", error)
            this._showError("Hanke koostamise tühistamine ebaõnnestus.")
        }
        finally {
            this._endAction(actionKey)
        }
    }


    _confirmArchiveTemplate(procurement: RHProcurerDashboardProcurement) {
        const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
            "Kas soovid hankemalli arhiveerida?",
            "Mall kaob aktiivsest valikust. Selle põhjal juba loodud hanked ei muutu ja kasutusstatistika säilib."
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Arhiveeri"
        shower.dialogView.view.noButton.titleLabel.text = "Sulge"
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._archiveTemplate(procurement)
        }
    }


    _confirmDeleteTemplate(procurement: RHProcurerDashboardProcurement) {
        const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
            "Kas soovid hankemalli kustutada?",
            "Kasutamata hankemall kustutatakse jäädavalt. Kui malli on vahepeal kasutatud, võib server kustutamisest keelduda."
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Kustuta"
        shower.dialogView.view.noButton.titleLabel.text = "Sulge"
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._deleteTemplate(procurement)
        }
    }


    async _archiveTemplate(procurement: RHProcurerDashboardProcurement) {
        await this._runTemplateMutation(procurement, "archive", (companyId, templateId, version) =>
            RHCore.sharedInstance.procurementStore.archiveProcurementTemplate({
                companyId,
                templateId,
                expectedTemplateUpdatedAt: version
            })
        )
    }


    async _restoreTemplate(procurement: RHProcurerDashboardProcurement) {
        await this._runTemplateMutation(procurement, "restore", (companyId, templateId, version) =>
            RHCore.sharedInstance.procurementStore.restoreProcurementTemplate({
                companyId,
                templateId,
                expectedTemplateUpdatedAt: version
            })
        )
    }


    async _deleteTemplate(procurement: RHProcurerDashboardProcurement) {
        await this._runTemplateMutation(procurement, "delete", (companyId, templateId, version) =>
            RHCore.sharedInstance.procurementStore.deleteUnusedProcurementTemplate({
                companyId,
                templateId,
                expectedTemplateUpdatedAt: version
            })
        )
    }


    async _runTemplateMutation(
        procurement: RHProcurerDashboardProcurement,
        action: string,
        mutation: (
            companyId: string,
            templateId: string,
            version: string
        ) => Promise<{ success: boolean; errorCode?: string }>
    ) {
        const companyId = this._activateRowCompany(procurement)
        const templateId = procurement._id?.toString()
        const version = procurement.templateUpdatedAt
            ? new Date(procurement.templateUpdatedAt).toISOString()
            : undefined
        if (!companyId || !templateId || !version) {
            this._showError("Hankemalli versiooniandmed puuduvad.")
            return
        }
        const actionKey = this._actionKey(procurement, action)
        if (!this._beginAction(actionKey)) {
            return
        }
        try {
            const result = await mutation(companyId, templateId, version)
            if (!result.success) {
                const message = this._messageForTemplateMutationError(result.errorCode)
                await this._reloadData()
                this._showError(message)
                return
            }
            await this._reloadData()
        }
        catch (error) {
            console.error("DraftProcurementsViewController: template mutation failed", error)
            this._showError("Hankemalli toiming ebaõnnestus.")
        }
        finally {
            this._endAction(actionKey)
        }
    }


    async _exportTemplateUsage(procurement: RHProcurerDashboardProcurement) {
        const companyId = this._activateRowCompany(procurement)
        const templateId = procurement._id?.toString()
        if (!companyId || !templateId) {
            return
        }
        const actionKey = this._actionKey(procurement, "xlsx")
        if (!this._beginAction(actionKey)) {
            return
        }
        try {
            const result = await RHCore.sharedInstance.procurementStore
                .exportProcurementTemplateUsageXLSX({ companyId, templateId })
            if (!result.success) {
                const message = this._messageForTemplateMutationError(result.errorCode)
                await this._reloadData()
                this._showError(message)
                return
            }
            ProcurementExcelExportDownloader.download(result.value)
            if (result.value.warnings.length > 0) {
                this._showWarning(
                    `XLSX loodi, kuid ${result.value.warnings.length} rea andmed vajavad kontrollimist.`
                )
            }
        }
        catch (error) {
            console.error("DraftProcurementsViewController: XLSX export failed", error)
            this._showError("XLSX loomine ebaõnnestus.")
        }
        finally {
            this._endAction(actionKey)
        }
    }


    async _copyTemplateLink(procurement: RHProcurerDashboardProcurement) {
        const templateId = procurement._id?.toString()
        if (!templateId) {
            return
        }
        const route = UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed([])
            .routeWithViewControllerComponent(NewProcurementViewController, { templateId })
        const baseUrl = ("" + window.location.href).split("#")[0]
        const didCopy = await RHClipboard.copy(baseUrl + route.linkRepresentation)
        if (didCopy) {
            this._showWarning("Hankemalli link kopeeriti.", RHAlertBannerView.type.Success)
        }
        else {
            this._showError("Hankemalli lingi kopeerimine ebaõnnestus.")
        }
    }


    _actionKey(procurement: RHProcurerDashboardProcurement, action: string): string {
        return `${this._companyIdForRow(procurement)}:${procurement._id}:${action}`
    }


    _rowHasPendingAction(procurement: RHProcurerDashboardProcurement): boolean {
        const prefix = `${this._companyIdForRow(procurement)}:${procurement._id}:`
        return Array.from(this._pendingActions).some(key => key.startsWith(prefix))
    }


    _beginAction(key: string): boolean {
        if (this._pendingActions.has(key)) {
            return NO
        }
        this._pendingActions.add(key)
        this._applyCategoryFilter()
        return YES
    }


    _endAction(key: string) {
        this._pendingActions.delete(key)
        this._applyCategoryFilter()
    }


    _messageForTemplateMutationError(errorCode?: string): string {
        if (errorCode === "TEMPLATE_VERSION_CONFLICT") {
            return "Hankemall on vahepeal muutunud. Andmed laaditi uuesti."
        }
        if (errorCode === "TEMPLATE_ALREADY_USED") {
            return "Kasutatud hankemalli ei saa kustutada. Arhiveeri mall."
        }
        if (errorCode === "NO_PUBLISHED_TEMPLATE_USAGE") {
            return "Malli põhjal pole veel avaldatud hankeid."
        }
        return "Hankemalli toiming ebaõnnestus."
    }


    _showError(text: string) {
        this.errorBanner.configureBannerWithObject({ alertType: RHAlertBannerView.type.Error, text })
        this.errorBanner.addedAsSubviewToView(this.view)
        this.retryButton.addedAsSubviewToView(this.view)
        this.view.setNeedsLayoutUpToRootView()
    }


    _showWarning(text: string, alertType: string = RHAlertBannerView.type.Warning) {
        this.errorBanner.configureBannerWithObject({ alertType, text })
        this.errorBanner.addedAsSubviewToView(this.view)
        this.retryButton.removeFromSuperview()
        this.view.setNeedsLayoutUpToRootView()
    }


    _applyTableColumnLayout() {
        const cellWeights = this._currentColumnWeights()
        this.procurementsDataView.tableHeaderView.configureWithObject({
            cellWeights,
            intrinsicContentHeight: () => 50
        })
        this.procurementsDataView.tableView.configureWithObject({ cellWeights })
        for (const headerCell of this.procurementsDataView.tableHeaderView.cells) {
            const titleLabel = headerCell.buttonView?.titleLabel ?? headerCell.titleLabel
            if (titleLabel) {
                titleLabel.isSingleLine = NO
                titleLabel.style.whiteSpace = "normal"
                titleLabel.style.lineHeight = RHDraftProcurementsViewTokens.multilineHeaderLineHeight
            }
        }
    }


    _currentColumnWeights(): number[] {
        const baseWeights = RHDraftProcurementsViewTokens.columnWeights
        if (!this._showsCompanyColumn) {
            return baseWeights
        }
        return [
            ...baseWeights.slice(0, 4),
            RHDraftProcurementsViewTokens.companyColumnWeight,
            ...baseWeights.slice(4)
        ]
    }


    _updateCompanyColumnVisibility(showsCompanyColumn: boolean) {
        if (this._showsCompanyColumn === showsCompanyColumn) {
            return
        }
        this._showsCompanyColumn = showsCompanyColumn
        this.procurementsDataView.descriptors = this._buildDescriptors()
        this._applyTableColumnLayout()
    }


    async _refreshCompanyScopeAffordances() {
        await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
        if (!this._hasAppliedDefaultCompanyScope) {
            this._hasAppliedDefaultCompanyScope = YES
            this._isAllCompaniesScopeActive =
                RHCore.sharedInstance.companyAffiliationStore.defaultAllCompaniesScopeActive
            this.filterAllCompaniesButton.chipActive = this._isAllCompaniesScopeActive
        }
        const hasMultipleCompanies =
            RHCore.sharedInstance.companyAffiliationStore.hasMultipleCompanyAffiliations
        this.filterAllCompaniesButton.enabled = hasMultipleCompanies
        this._updateCompanyColumnVisibility(
            hasMultipleCompanies && this._isAllCompaniesScopeActive
        )
        this.filterChipBar.chips = this._visibleFilterChips()
        this.view.setNeedsLayout()
    }


    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
        const parameters = route.componentWithName(UserMainViewController.routeComponentName)?.parameters
        if (
            parameters?.[UserMainViewController.ParameterIdentifierName.tab] !== "drafts" ||
            parameters?.[UserMainViewController.ParameterIdentifierName.draftCategory] !== "draft"
        ) {
            return
        }

        this._activeCategories = new Set<DraftCategory>(["draft"])
        this.filterDraftsButton.chipActive = YES
        this.filterTemplatesButton.chipActive = NO
        this.filterArchivedButton.chipActive = NO
        this.procurementsDataView.searchTextField.text = ""
        this._applyCategoryFilter()
        await route
            .routeByRemovingParameterInComponent(
                UserMainViewController.routeComponentName,
                UserMainViewController.ParameterIdentifierName.draftCategory
            )
            .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
    }


    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
        RHCore.sharedInstance.procurementStore.subscribe(this, () => void this._reloadData())
        await this._refreshCompanyScopeAffordances()
        await this._reloadData()
    }


    override async viewWillDisappear() {
        this._openActionsView?.dismissMenu()
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
        RHCore.sharedInstance.procurementStore.unsubscribe(this)
    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.companyAffiliationsDidChange) {
            this._openActionsView?.dismissMenu()
            void this._refreshCompanyScopeAffordances().then(() => this._reloadData())
        }
    }


    async _reloadData() {
        this._openActionsView?.dismissMenu()
        this.errorBanner.removeFromSuperview()
        this.retryButton.removeFromSuperview()
        this.procurementsDataView.loading = YES
        try {
            const activeDashboardPromise = RHCore.sharedInstance.procurementStore
                .retrieveOwnProcurementDashboard(["draft", "template"])
            let rows: RHProcurerDashboardProcurement[]
            if (this._isAllCompaniesScopeActive) {
                rows = await RHCore.sharedInstance.procurementStore
                    .retrieveOwnProcurementsAcrossAllMyCompanies(["draft", "template"])
            }
            else {
                rows = (await activeDashboardPromise).rows
            }
            const activeDashboard = await activeDashboardPromise
            this._canCreateTemplate = activeDashboard.companyCapabilities.canCreateTemplate
            this.createTemplateButton.hidden = !this._canCreateTemplate
            this.createTemplateButton.enabled = this._canCreateTemplate
            this.procurementsDataView.setNeedsLayout()
            this._allRows = rows.slice().sort((first, second) =>
                this._creationSortValueForProcurement(second)
                    .localeCompare(this._creationSortValueForProcurement(first))
            )
            this._applyCategoryFilter()
        }
        catch (error) {
            console.error("DraftProcurementsViewController: data load failed", error)
            this._allRows = []
            this.procurementsDataView.data = []
            this._showError("Andmete laadimine ebaõnnestus.")
        }
        finally {
            this.procurementsDataView.loading = NO
            this._updateNoDataMessage()
            this.view.setNeedsLayout()
        }
    }


    _updateNoDataMessage() {
        const hasSearch = this.procurementsDataView.searchTextField.text.trim().length > 0
        if (this._activeCategories.size > 0 && this.procurementsDataView.filteredData.length === 0) {
            this.procurementsDataView.noDataMessage = "Valitud kategooriates vasteid pole."
        }
        else if (hasSearch && this.procurementsDataView.filteredData.length === 0) {
            this.procurementsDataView.noDataMessage = "Otsingule vastavaid ettevalmistamisel olevaid hankeid ega malle pole."
        }
        else {
            this.procurementsDataView.noDataMessage = "Mallid puuduvad"
        }
    }


    _creationDateForProcurement(procurement: RHProcurerDashboardProcurement): Date | undefined {
        const createdAt = RHTimestampSorting.sortableISOString(procurement.createdAt)
        if (createdAt) {
            return new Date(createdAt)
        }
        const objectId = procurement._id?.toString()
        if (!objectId || objectId.length < 8) {
            return undefined
        }
        const timestamp = parseInt(objectId.slice(0, 8), 16)
        if (isNaN(timestamp)) {
            return undefined
        }
        return new Date(timestamp * 1000)
    }


    _creationSortValueForProcurement(procurement: RHProcurerDashboardProcurement): string {
        return this._creationDateForProcurement(procurement)?.toISOString() ?? ""
    }


    override layoutViewSubviews() {
        super.layoutViewSubviews()
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
            .rectangleWithMaxWidth(RHDraftProcurementsViewTokens.contentMaxWidth, 0.5)

        let current = bounds
        if (this.errorBanner.superview === this.view) {
            current = current.rectangleWithHeight(this.errorBanner)
                .assignedAsFrameOfView(this.errorBanner)
            if (this.retryButton.superview === this.view) {
                current = current.rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
                    .rectangleWithWidth(RHDraftProcurementsViewTokens.headerActionWidth, 0)
                    .assignedAsFrameOfView(this.retryButton)
            }
            current = current.rectangleForNextRow(padding)
        }

        current = current.rectangleWithHeight(this.filterChipBar)
            .assignedAsFrameOfView(this.filterChipBar)
        const dataFrame = current
            .rectangleForNextRow(padding, RHDraftProcurementsViewTokens.tableHeight)
            .assignedAsFrameOfView(this.procurementsDataView)

        dataFrame.rectangleForNextRow(padding, this.bottomView)
            .assignedAsFrameOfView(this.bottomView)
    }
}
